import { Injectable } from '@angular/core';
import { OfficeNumberRounding, TaxBaseType } from '../models/enums';
import { ITaxBeforeStakeDefinitionTO } from '../models/settings/transportObjects/taxBeforeStakeDefinitionTO.interface';
import { ITaxOnWinningsDefinitionTO } from '../models/settings/transportObjects/taxOnWinningsDefinitionTO.interface';
import { ITaxOnWinningsCalculationResult } from '../models/taxOnWinningsCalculationResult';
import { UtilityService } from './util/utility.service';
import { IWinningTaxTO } from '../models/settings/transportObjects/winningTaxTO.interface';

@Injectable()
export class TaxCalculatorService {


    constructor(private utilityService: UtilityService) {
    }

    public calculateTaxBeforeStake(definition: ITaxBeforeStakeDefinitionTO, payinAmount: number): number {

        if (!definition)
        {
            return 0;
        }

        if (payinAmount === 0)
        {
            return 0;
        }

        const result  = (definition.stakePercent / 100) * payinAmount;
        return this.utilityService.roundByBusinessModel(result);
    }


    public calculateTaxOnWinnings(definition: ITaxOnWinningsDefinitionTO, winningAmount: number, stakeAmount: number): ITaxOnWinningsCalculationResult {

            if (!definition){
                return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
            }

            if (definition.taxBaseType === TaxBaseType.NetWinning && winningAmount < stakeAmount){
                return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
            }


            const taxBase: number = definition.taxBaseType === TaxBaseType.Winning ? winningAmount : (winningAmount - stakeAmount);

            if (taxBase === 0){
                return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
            }

            const taxDefinitions: Array<IWinningTaxTO> = new Array<IWinningTaxTO>();

            if (definition.tax1) { taxDefinitions.push(definition.tax1); }
            if (definition.tax2) { taxDefinitions.push(definition.tax2); }
            if (definition.tax3) { taxDefinitions.push(definition.tax3); }
            if (definition.tax4) { taxDefinitions.push(definition.tax4); }

            if (taxDefinitions.length === 0){
                return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
            }

            if (taxBase <= taxDefinitions[0].winningFrom){
                return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
            }

            taxDefinitions[taxDefinitions.length - 1].winningTo = null;

            const ranges: Array<DecimalRange>  = taxDefinitions.map(x => ({ start: x.winningFrom ?? 0, end: x.winningTo ?? Number.MAX_VALUE}));

            if (!this.rangesOverlappingOnEdges(ranges))
            {
                return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
            }


            const res = this.calculateTaxOnWinning(taxBase, definition.taxOnLumpSum, taxDefinitions);

            return this.roundWinningsCalculationResult(res);
    }

    private roundWinningsCalculationResult(calculation: ITaxOnWinningsCalculationResult ){
            const taxOnWinnings1 = this.utilityService.roundByBusinessModel(calculation.taxOnWinnings1);
            const taxOnWinnings2 = this.utilityService.roundByBusinessModel(calculation.taxOnWinnings2);
            const taxOnWinnings3 = this.utilityService.roundByBusinessModel(calculation.taxOnWinnings3);
            const taxOnWinnings4 = this.utilityService.roundByBusinessModel(calculation.taxOnWinnings4);

            return {taxOnWinnings1, taxOnWinnings2, taxOnWinnings3, taxOnWinnings4};
    }



    private calculateTaxOnWinning(taxBase: number, taxOnLumpSum: boolean, taxDefinitions: Array<IWinningTaxTO>): ITaxOnWinningsCalculationResult {
        if (taxBase < 0){
            return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
        }

        if (taxBase === 0){
            return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
        }

        if (taxOnLumpSum){

            const taxDefinition: IWinningTaxTO = taxDefinitions.find(x => (taxBase > (x.winningFrom ?? 0) && taxBase <= (x.winningTo ?? Number.MAX_VALUE)));

            if (!taxDefinition){
                return {taxOnWinnings1: 0, taxOnWinnings2: 0, taxOnWinnings3: 0, taxOnWinnings4: 0 };
            }

            const result: number = taxBase * (taxDefinition.taxPercent / 100);

            return {
                taxOnWinnings1: taxDefinition.orderNumber === 1 ? result : 0,
                taxOnWinnings2: taxDefinition.orderNumber === 2 ? result : 0,
                taxOnWinnings3: taxDefinition.orderNumber === 3 ? result : 0,
                taxOnWinnings4: taxDefinition.orderNumber === 4 ? result : 0
            };


        }
        else{
            let taxOnWinnings1 = 0;
            let taxOnWinnings2 = 0;
            let taxOnWinnings3 = 0;
            let taxOnWinnings4 = 0;

            taxDefinitions.forEach(taxDefinition => {
                const stakeFrom: number = taxDefinition.winningFrom ?? 0;
                let stakeTo: number  = taxDefinition.winningTo ?? Number.MAX_VALUE;
                if (stakeFrom < taxBase && stakeTo > taxBase)
                {
                    stakeTo = taxBase;
                }
                let taxValue = 0;
                if (stakeFrom < taxBase)
                {
                    taxValue = (stakeTo - stakeFrom) * (taxDefinition.taxPercent / 100);
                }

                if (taxDefinition.orderNumber === 1)
                {
                    taxOnWinnings1 = taxValue;
                }
                else if (taxDefinition.orderNumber === 2)
                {
                    taxOnWinnings2 = taxValue;
                }
                else if (taxDefinition.orderNumber === 3)
                {
                    taxOnWinnings3 = taxValue;
                }
                else if (taxDefinition.orderNumber === 4)
                {
                    taxOnWinnings4 = taxValue;
                }
            });

            return {taxOnWinnings1, taxOnWinnings2, taxOnWinnings3, taxOnWinnings4};
        }

    }

    private rangesOverlappingOnEdges(ranges: Array<DecimalRange> ): boolean
    {
        for (let i = 1; i < ranges.length; i++)
        {
            if (ranges[i].start !== ranges[i - 1].end)
            {
                return false;
            }
        }

        return true;
    }
}


export interface DecimalRange {
    start: number;
    end: number;
}
